import React, { useState, useEffect } from "react";
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    ComposedChart
} from "recharts";
import {
    MapPin,
    Droplets,
    Wind,
    Eye,
    Gauge,
    Sun,
    Sunrise,
    Sunset,
    CloudRain,
    Activity,
    Thermometer,
    Sprout,
    AlertTriangle,
    CheckCircle,
    Info
} from "lucide-react";
import "../style/WeatherPage.css";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";

const WeatherPage = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [weatherData, setWeatherData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [location, setLocation] = useState("");
    const [error, setError] = useState(null);

    const API_KEY = "4d8fb5b93d4af21d66a2948710284366";

    useEffect(() => {
        fetchWeatherData();
        const interval = setInterval(fetchWeatherData, 600000);
        return () => clearInterval(interval);
    }, []);

    const fetchWeatherData = async () => {
        setLoading(true);
        setError(null);

        try {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        await loadWeatherData(latitude, longitude);
                    },
                    async () => {
                        await loadWeatherData(6.2476, -75.5658);
                    }
                );
            } else {
                await loadWeatherData(6.2476, -75.5658);
            }
        } catch (err) {
            console.error("Error cargando clima:", err);
            setError("No se pudo cargar la información del clima");
            setLoading(false);
        }
    };

    const loadWeatherData = async (lat, lon) => {
        try {
            const currentResponse = await fetch(
                `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric&lang=es`
            );
            const currentData = await currentResponse.json();

            const forecastResponse = await fetch(
                `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric&lang=es`
            );
            const forecastData = await forecastResponse.json();

            const airResponse = await fetch(
                `https://api.openweathermap.org/data/2.5/air_pollution?lat=${lat}&lon=${lon}&appid=${API_KEY}`
            );
            const airData = await airResponse.json();

            processWeatherData(currentData, forecastData, airData);
            setLoading(false);
        } catch (err) {
            console.error("Error en API:", err);
            setError("Error al obtener datos del clima");
            setLoading(false);
        }
    };

    const calculateDewPoint = (temp, humidity) => {
        const a = 17.27;
        const b = 237.7;
        const alpha = ((a * temp) / (b + temp)) + Math.log(humidity / 100.0);
        const dewPoint = (b * alpha) / (a - alpha);
        return Math.round(dewPoint);
    };

    const processWeatherData = (current, forecast, air) => {
        const currentWeather = {
            temp: Math.round(current.main.temp),
            feelsLike: Math.round(current.main.feels_like),
            tempMin: Math.round(current.main.temp_min),
            tempMax: Math.round(current.main.temp_max),
            condition: current.weather[0].description,
            icon: getWeatherIcon(current.weather[0].main),
            humidity: current.main.humidity,
            windSpeed: Math.round(current.wind.speed * 3.6),
            windDirection: getWindDirection(current.wind.deg),
            pressure: current.main.pressure,
            visibility: (current.visibility / 1000).toFixed(1),
            dewPoint: calculateDewPoint(current.main.temp, current.main.humidity),
            cloudiness: current.clouds.all,
            airQuality: air.list[0].main.aqi,
            airQualityLabel: getAirQualityLabel(air.list[0].main.aqi),
            sunrise: formatTime(current.sys.sunrise),
            sunset: formatTime(current.sys.sunset),
            precipitation: current.rain ? current.rain["1h"] || 0 : 0,
            uvIndex: 5
        };

        const hourlyForecast = forecast.list.slice(0, 24).map(item => {
            const date = new Date(item.dt * 1000);
            return {
                time: date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                hour: date.getHours(),
                temp: Math.round(item.main.temp),
                humidity: item.main.humidity,
                windSpeed: Math.round(item.wind.speed * 3.6),
                pressure: item.main.pressure,
                condition: item.weather[0].main,
                icon: getWeatherIcon(item.weather[0].main),
                precipitation: Math.round(item.pop * 100),
                description: item.weather[0].description,
                cloudiness: item.clouds.all,
                dewPoint: calculateDewPoint(item.main.temp, item.main.humidity)
            };
        });

        const dailyForecast = processDailyForecast(forecast.list);
        const weatherChanges = detectWeatherChanges(hourlyForecast);
        const agriRecommendations = generateAgriRecommendations(currentWeather, hourlyForecast, dailyForecast);

        setWeatherData({
            current: currentWeather,
            hourly: hourlyForecast,
            daily: dailyForecast,
            changes: weatherChanges,
            recommendations: agriRecommendations
        });

        setLocation(`${current.name}, ${current.sys.country}`);
    };

    const processDailyForecast = (list) => {
        const dailyData = {};

        list.forEach(item => {
            const date = new Date(item.dt * 1000);
            const dateKey = date.toLocaleDateString('es-ES');

            if (!dailyData[dateKey]) {
                dailyData[dateKey] = {
                    date: date,
                    dayName: date.toLocaleDateString('es-ES', { weekday: 'short' }),
                    tempMax: item.main.temp_max,
                    tempMin: item.main.temp_min,
                    humidityMax: item.main.humidity,
                    humidityMin: item.main.humidity,
                    windMax: item.wind.speed * 3.6,
                    condition: item.weather[0].main,
                    icon: getWeatherIcon(item.weather[0].main),
                    precipitation: item.pop * 100,
                    description: item.weather[0].description
                };
            } else {
                dailyData[dateKey].tempMax = Math.max(dailyData[dateKey].tempMax, item.main.temp_max);
                dailyData[dateKey].tempMin = Math.min(dailyData[dateKey].tempMin, item.main.temp_min);
                dailyData[dateKey].humidityMax = Math.max(dailyData[dateKey].humidityMax, item.main.humidity);
                dailyData[dateKey].windMax = Math.max(dailyData[dateKey].windMax, item.wind.speed * 3.6);
                dailyData[dateKey].precipitation = Math.max(dailyData[dateKey].precipitation, item.pop * 100);
            }
        });

        return Object.values(dailyData).slice(0, 7).map(day => ({
            ...day,
            tempMax: Math.round(day.tempMax),
            tempMin: Math.round(day.tempMin),
            humidityMax: Math.round(day.humidityMax),
            windMax: Math.round(day.windMax),
            precipitation: Math.round(day.precipitation)
        }));
    };

    const detectWeatherChanges = (hourly) => {
        const changes = [];

        for (let i = 1; i < Math.min(hourly.length, 8); i++) {
            const prev = hourly[i - 1];
            const curr = hourly[i];

            if (Math.abs(curr.temp - prev.temp) > 3) {
                changes.push({
                    time: curr.time,
                    type: 'temperature',
                    icon: curr.temp > prev.temp ? '🌡️↑' : '🌡️↓',
                    message: `Cambio de temperatura: ${prev.temp}°C → ${curr.temp}°C`
                });
            }

            if (curr.precipitation > 60 && prev.precipitation <= 60) {
                changes.push({
                    time: curr.time,
                    type: 'rain',
                    icon: '☔',
                    message: `Alta probabilidad de lluvia (${curr.precipitation}%)`
                });
            }
        }

        return changes;
    };

    const generateAgriRecommendations = (current, hourly, daily) => {
        const recommendations = [];

        if (current.temp > 32) {
            recommendations.push({
                category: 'Temperatura',
                type: 'critical',
                icon: <AlertTriangle size={20} />,
                title: 'Temperatura muy alta',
                message: 'Riesgo de estrés térmico en plantas. Aumenta riego en horas tempranas.',
                actions: ['Riego abundante al amanecer', 'Mulching para conservar humedad', 'Proteger plantas jóvenes']
            });
        } else if (current.temp > 28) {
            recommendations.push({
                category: 'Temperatura',
                type: 'warning',
                icon: <Thermometer size={20} />,
                title: 'Temperatura alta',
                message: 'Aumenta frecuencia de riego especialmente en hortalizas.',
                actions: ['Riego 2 veces al día', 'Monitorear plantas delicadas']
            });
        } else if (current.temp < 15) {
            recommendations.push({
                category: 'Temperatura',
                type: 'warning',
                icon: <Thermometer size={20} />,
                title: 'Temperatura baja',
                message: 'Reduce riego. Protege cultivos sensibles al frío.',
                actions: ['Reducir frecuencia de riego', 'Proteger solanáceas']
            });
        } else {
            recommendations.push({
                category: 'Temperatura',
                type: 'good',
                icon: <CheckCircle size={20} />,
                title: 'Temperatura ideal',
                message: 'Condiciones óptimas para la mayoría de cultivos.',
                actions: ['Época ideal para trasplantes', 'Aplicar fertilizantes foliares']
            });
        }

        if (current.humidity > 85) {
            recommendations.push({
                category: 'Humedad',
                type: 'critical',
                icon: <AlertTriangle size={20} />,
                title: 'Humedad muy alta - Riesgo de hongos',
                message: 'Alto riesgo de enfermedades fúngicas. Mejora ventilación.',
                actions: ['Aplicar fungicidas preventivos', 'Mejorar ventilación', 'Evitar mojar follaje']
            });
        } else if (current.humidity > 70) {
            recommendations.push({
                category: 'Humedad',
                type: 'warning',
                icon: <Droplets size={20} />,
                title: 'Humedad alta',
                message: 'Condiciones favorables para hongos. Reduce riego.',
                actions: ['Reducir frecuencia de riego', 'Monitoreo de enfermedades']
            });
        } else if (current.humidity < 40) {
            recommendations.push({
                category: 'Humedad',
                type: 'warning',
                icon: <Droplets size={20} />,
                title: 'Humedad baja',
                message: 'Incrementa riego especialmente en cultivos de hoja.',
                actions: ['Riego más frecuente', 'Nebulización en horas frescas']
            });
        } else {
            recommendations.push({
                category: 'Humedad',
                type: 'good',
                icon: <CheckCircle size={20} />,
                title: 'Humedad óptima',
                message: 'Nivel ideal para crecimiento vegetal.',
                actions: ['Mantener programa de riego actual', 'Realizar podas programadas']
            });
        }

        if (current.windSpeed > 40) {
            recommendations.push({
                category: 'Viento',
                type: 'critical',
                icon: <AlertTriangle size={20} />,
                title: 'Viento muy fuerte',
                message: 'Peligro de daño mecánico en cultivos.',
                actions: ['Reforzar tutores inmediatamente', 'NO aplicar productos']
            });
        } else if (current.windSpeed > 25) {
            recommendations.push({
                category: 'Viento',
                type: 'warning',
                icon: <Wind size={20} />,
                title: 'Viento fuerte',
                message: 'Revisa tutores. Pospón fumigaciones.',
                actions: ['Verificar tutores', 'Posponer fumigaciones']
            });
        } else if (current.windSpeed >= 8 && current.windSpeed <= 20) {
            recommendations.push({
                category: 'Viento',
                type: 'good',
                icon: <CheckCircle size={20} />,
                title: 'Viento favorable',
                message: 'Velocidad ideal para polinización natural.',
                actions: ['Época ideal para aplicaciones', 'Favorecer polinización natural']
            });
        }

        const nextRain = hourly.find(h => h.precipitation > 60);
        if (nextRain) {
            const hoursToRain = hourly.indexOf(nextRain) * 3;
            recommendations.push({
                category: 'Lluvia',
                type: 'warning',
                icon: <CloudRain size={20} />,
                title: `Lluvia esperada en ${hoursToRain} horas`,
                message: 'Suspende riego programado. Verifica drenajes.',
                actions: ['Cancelar riego', 'Verificar canales de drenaje']
            });
        }

        if (current.airQuality >= 4) {
            recommendations.push({
                category: 'Calidad del Aire',
                type: 'warning',
                icon: <Activity size={20} />,
                title: 'Calidad del aire mala',
                message: 'Polución puede reducir fotosíntesis. Limpia follaje.',
                actions: ['Lavar follaje con agua', 'Monitorear crecimiento']
            });
        }

        if (current.temp > 28 && current.humidity < 40 && current.windSpeed > 20) {
            recommendations.push({
                category: 'Condiciones Extremas',
                type: 'critical',
                icon: <AlertTriangle size={20} />,
                title: 'Estrés hídrico extremo',
                message: 'Combinación peligrosa: calor + viento + sequedad.',
                actions: ['Riego de emergencia inmediato', 'Nebulización frecuente']
            });
        }

        if (current.temp > 25 && current.humidity > 80) {
            recommendations.push({
                category: 'Condiciones Extremas',
                type: 'critical',
                icon: <AlertTriangle size={20} />,
                title: 'Condiciones perfectas para hongos',
                message: 'Combinación ideal para explosión de enfermedades fúngicas.',
                actions: ['Aplicar fungicidas YA', 'Mejorar drenaje', 'Aumentar ventilación']
            });
        }

        const avgTempNext3Days = daily.slice(0, 3).reduce((sum, d) => sum + (d.tempMax + d.tempMin) / 2, 0) / 3;
        if (avgTempNext3Days >= 20 && avgTempNext3Days <= 26) {
            recommendations.push({
                category: 'Momento Ideal',
                type: 'good',
                icon: <Sprout size={20} />,
                title: 'Condiciones óptimas próximos 3 días',
                message: 'Clima ideal para siembra, trasplante y podas.',
                actions: ['Realizar trasplantes pendientes', 'Hacer podas programadas', 'Aplicar fertilizantes']
            });
        }

        return recommendations;
    };

    const getWeatherIcon = (condition) => {
        const icons = {
            'Clear': '☀️',
            'Clouds': '☁️',
            'Rain': '🌧️',
            'Drizzle': '🌦️',
            'Thunderstorm': '⛈️',
            'Snow': '❄️',
            'Mist': '🌫️',
            'Fog': '🌫️',
            'Haze': '🌫️'
        };
        return icons[condition] || '☁️';
    };

    const getWindDirection = (degrees) => {
        const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
        return directions[Math.round(degrees / 22.5) % 16];
    };

    const getAirQualityLabel = (aqi) => {
        const labels = {
            1: { text: 'Buena', color: '#10b981' },
            2: { text: 'Aceptable', color: '#84cc16' },
            3: { text: 'Moderada', color: '#f59e0b' },
            4: { text: 'Mala', color: '#ef4444' },
            5: { text: 'Muy Mala', color: '#dc2626' }
        };
        return labels[aqi] || labels[3];
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="page-root">
                <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
                <div className="page-main">
                    <Header isOpen={isOpen} setIsOpen={setIsOpen} />
                    <div className="page-content">
                        <div className="weather-loading">
                            <div className="spinner"></div>
                            <p>Obteniendo información del clima...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-root">
                <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
                <div className="page-main">
                    <Header isOpen={isOpen} setIsOpen={setIsOpen} />
                    <div className="page-content">
                        <div className="weather-error">
                            <p>{error}</p>
                            <button onClick={fetchWeatherData} className="retry-button">
                                Intentar de nuevo
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-root">
            <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
            <div className="page-main">
                <Header isOpen={isOpen} setIsOpen={setIsOpen} />
                <div className="page-content">
                    <div className="weather-container">
                        <div className="weather-top">
                            <div className="location-info">
                                <MapPin size={24} />
                                <div>
                                    <h1>{location}</h1>
                                    <p>{new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                                </div>
                            </div>
                            <button onClick={fetchWeatherData} className="refresh-button">Actualizar</button>
                        </div>

                        <div className="current-weather-card">
                            <div className="current-left">
                                <div className="main-temp">
                                    <span className="temp-icon">{weatherData.current.icon}</span>
                                    <div>
                                        <div className="temp-big">{weatherData.current.temp}°</div>
                                        <p className="condition-text">{weatherData.current.condition}</p>
                                        <p className="feels-like-text">Sensación de {weatherData.current.feelsLike}°</p>
                                        <p className="temp-range">Min: {weatherData.current.tempMin}° / Max: {weatherData.current.tempMax}°</p>
                                    </div>
                                </div>
                            </div>
                            <div className="current-right">
                                <div className="weather-detail-item">
                                    <Droplets size={20} />
                                    <div>
                                        <span className="detail-label">Humedad</span>
                                        <span className="detail-value">{weatherData.current.humidity}%</span>
                                    </div>
                                </div>
                                <div className="weather-detail-item">
                                    <Wind size={20} />
                                    <div>
                                        <span className="detail-label">Viento</span>
                                        <span className="detail-value">{weatherData.current.windSpeed} km/h</span>
                                    </div>
                                </div>
                                <div className="weather-detail-item">
                                    <CloudRain size={20} />
                                    <div>
                                        <span className="detail-label">Precipitación</span>
                                        <span className="detail-value">{weatherData.current.precipitation} mm</span>
                                    </div>
                                </div>
                                <div className="weather-detail-item">
                                    <Gauge size={20} />
                                    <div>
                                        <span className="detail-label">Presión</span>
                                        <span className="detail-value">{weatherData.current.pressure} hPa</span>
                                    </div>
                                </div>
                                <div className="weather-detail-item">
                                    <Thermometer size={20} />
                                    <div>
                                        <span className="detail-label">Punto de Rocío</span>
                                        <span className="detail-value">{weatherData.current.dewPoint}°</span>
                                    </div>
                                </div>
                                <div className="weather-detail-item">
                                    <Activity size={20} />
                                    <div>
                                        <span className="detail-label">Calidad del aire</span>
                                        <span className="detail-value" style={{ color: weatherData.current.airQualityLabel.color }}>
                                            {weatherData.current.airQualityLabel.text}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {weatherData.changes.length > 0 && (
                            <div className="weather-changes-card">
                                <h3>⚡ Cambios esperados en el clima</h3>
                                <div className="changes-list">
                                    {weatherData.changes.map((change, index) => (
                                        <div key={index} className="change-item">
                                            <span className="change-icon">{change.icon}</span>
                                            <span className="change-time">{change.time}</span>
                                            <span className="change-message">{change.message}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="charts-grid">
                            <div className="forecast-card">
                                <h3>🌡️ Temperatura</h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <AreaChart data={weatherData.hourly.slice(0, 8)}>
                                        <defs>
                                            <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} />
                                        <YAxis stroke="#9ca3af" fontSize={11} />
                                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                                        <Area type="monotone" dataKey="temp" stroke="#ef4444" fill="url(#tempGradient)" strokeWidth={2} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="forecast-card">
                                <h3>💧 Humedad</h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <AreaChart data={weatherData.hourly.slice(0, 8)}>
                                        <defs>
                                            <linearGradient id="humidityGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} />
                                        <YAxis stroke="#9ca3af" fontSize={11} domain={[0, 100]} />
                                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                                        <Area type="monotone" dataKey="humidity" stroke="#3b82f6" fill="url(#humidityGradient)" strokeWidth={2} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="forecast-card">
                                <h3>💨 Viento</h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={weatherData.hourly.slice(0, 8)}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} />
                                        <YAxis stroke="#9ca3af" fontSize={11} />
                                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                                        <Bar dataKey="windSpeed" fill="#10b981" radius={[8, 8, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="forecast-card">
                                <h3>☔ Probabilidad de Lluvia</h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={weatherData.hourly.slice(0, 8)}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} />
                                        <YAxis stroke="#9ca3af" fontSize={11} domain={[0, 100]} />
                                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                                        <Bar dataKey="precipitation" fill="#6366f1" radius={[8, 8, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="forecast-card">
                            <h3>📊 Condiciones Combinadas (24 horas)</h3>
                            <ResponsiveContainer width="100%" height={280}>
                                <ComposedChart data={weatherData.hourly}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} />
                                    <YAxis yAxisId="left" stroke="#ef4444" fontSize={11} />
                                    <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" fontSize={11} domain={[0, 100]} />
                                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                                    <Legend />
                                    <Line yAxisId="left" type="monotone" dataKey="temp" stroke="#ef4444" strokeWidth={2} name="Temperatura (°C)" dot={false} />
                                    <Line yAxisId="right" type="monotone" dataKey="humidity" stroke="#3b82f6" strokeWidth={2} name="Humedad (%)" dot={false} />
                                    <Bar yAxisId="right" dataKey="precipitation" fill="#6366f1" opacity={0.6} name="Lluvia (%)" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="forecast-card">
                            <h3>📅 Pronóstico de 7 Días</h3>
                            <div className="daily-list">
                                {weatherData.daily.map((day, index) => (
                                    <div key={index} className="daily-item-row">
                                        <span className="daily-day">{day.dayName}</span>
                                        <span className="daily-icon-big">{day.icon}</span>
                                        <span className="daily-description">{day.description}</span>
                                        <div className="daily-temps">
                                            <span className="temp-high">{day.tempMax}°</span>
                                            <div className="temp-bar"></div>
                                            <span className="temp-low">{day.tempMin}°</span>
                                        </div>
                                        <div className="daily-detail">
                                            <Droplets size={14} />
                                            <span>{day.humidityMax}%</span>
                                        </div>
                                        <div className="daily-detail">
                                            <Wind size={14} />
                                            <span>{day.windMax} km/h</span>
                                        </div>
                                        <div className="daily-detail">
                                            <CloudRain size={14} />
                                            <span>{day.precipitation}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="recommendations-section">
                            <h2>🌱 Recomendaciones para tus Cultivos</h2>

                            {weatherData.recommendations.filter(r => r.type === 'critical').length > 0 && (
                                <div className="recommendations-category critical">
                                    <h3><AlertTriangle size={20} /> Alertas Críticas - Acción Inmediata</h3>
                                    <div className="recommendations-grid">
                                        {weatherData.recommendations.filter(r => r.type === 'critical').map((rec, index) => (
                                            <div key={index} className="recommendation-card-detailed">
                                                <div className="rec-header">
                                                    <div className="rec-icon-detailed">{rec.icon}</div>
                                                    <div>
                                                        <div className="rec-category">{rec.category}</div>
                                                        <h4>{rec.title}</h4>
                                                    </div>
                                                </div>
                                                <p className="rec-message">{rec.message}</p>
                                                <div className="rec-actions">
                                                    <strong>Acciones recomendadas:</strong>
                                                    <ul>
                                                        {rec.actions.map((action, i) => (
                                                            <li key={i}>{action}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {weatherData.recommendations.filter(r => r.type === 'warning').length > 0 && (
                                <div className="recommendations-category warning">
                                    <h3><Info size={20} /> Advertencias - Precaución</h3>
                                    <div className="recommendations-grid">
                                        {weatherData.recommendations.filter(r => r.type === 'warning').map((rec, index) => (
                                            <div key={index} className="recommendation-card-detailed">
                                                <div className="rec-header">
                                                    <div className="rec-icon-detailed">{rec.icon}</div>
                                                    <div>
                                                        <div className="rec-category">{rec.category}</div>
                                                        <h4>{rec.title}</h4>
                                                    </div>
                                                </div>
                                                <p className="rec-message">{rec.message}</p>
                                                <div className="rec-actions">
                                                    <strong>Acciones recomendadas:</strong>
                                                    <ul>
                                                        {rec.actions.map((action, i) => (
                                                            <li key={i}>{action}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {weatherData.recommendations.filter(r => r.type === 'good').length > 0 && (
                                <div className="recommendations-category good">
                                    <h3><CheckCircle size={20} /> Condiciones Favorables</h3>
                                    <div className="recommendations-grid">
                                        {weatherData.recommendations.filter(r => r.type === 'good').map((rec, index) => (
                                            <div key={index} className="recommendation-card-detailed">
                                                <div className="rec-header">
                                                    <div className="rec-icon-detailed">{rec.icon}</div>
                                                    <div>
                                                        <div className="rec-category">{rec.category}</div>
                                                        <h4>{rec.title}</h4>
                                                    </div>
                                                </div>
                                                <p className="rec-message">{rec.message}</p>
                                                <div className="rec-actions">
                                                    <strong>Aprovecha para:</strong>
                                                    <ul>
                                                        {rec.actions.map((action, i) => (
                                                            <li key={i}>{action}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="sun-box">
                            <h4><Sun size={20} /> Información Solar</h4>
                            <div className="sun-times">
                                <div className="sun-item">
                                    <Sunrise size={18} />
                                    <span>Amanecer</span>
                                    <strong>{weatherData.current.sunrise}</strong>
                                </div>
                                <div className="sun-item">
                                    <Sunset size={18} />
                                    <span>Atardecer</span>
                                    <strong>{weatherData.current.sunset}</strong>
                                </div>
                                <div className="sun-item">
                                    <Sun size={18} />
                                    <span>Nubosidad</span>
                                    <strong>{weatherData.current.cloudiness}%</strong>
                                </div>
                                <div className="sun-item">
                                    <Eye size={18} />
                                    <span>Visibilidad</span>
                                    <strong>{weatherData.current.visibility} km</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WeatherPage;